/**
 * Utility class:
 *  carousel component
 * markup expectation: <parent><carousel> <card></card> <card></card>...</carousel></parent>
 * */
class Carousel {
    private readonly parent: HTMLElement;
    private readonly container: HTMLElement;
    private readonly direction: 'left' | 'right';
    private readonly delayAfterStop: number;
    private readonly onClick?: boolean;
    private readonly onHover?: boolean;
    private readonly dragAndScroll?: boolean;
    private readonly autoscroll?: boolean;
    private readonly Scroll?: boolean;
    private readonly sliderControls?: SliderControls;
    private readonly card: string;
    private readonly speed: number = 0.1;
    private readonly gap: number = 10;
    private isPaused: boolean = false;
    private isDragging: boolean = false;
    private dragStartX: number | null = null;
    private dragOffset: number = 0;
    private animationFrameId: number | null = null;
    private lastTimestamp: number = 0;
    private cardWidth: number = 0;
    private totalSetWidth: number = 0;

    constructor(config: CarouselConfig) {
        const {
            parent,
            direction,
            delayAfterStop,
            onclick,
            onhover,
            autoscroll,
            dragAndScroll,
            speed,
            infinityScroll,
            sliderControls,
            templateName,
            gap
        } = config;
        this.parent = parent;
        this.container = this.parent.querySelector(templateName.container)!;
        this.direction = direction;
        this.delayAfterStop = delayAfterStop;
        this.onClick = onclick;
        this.onHover = onhover;
        this.dragAndScroll = dragAndScroll;
        this.autoscroll = autoscroll;
        this.Scroll = infinityScroll;
        this.sliderControls = sliderControls;
        this.card = templateName.card;
        this.speed = speed ?? 0.1;
        this.gap = gap;
        this.setupClones();
        this.setupEvents();
        this.setUpSliderControls();
        if (this.autoscroll) this.startAnimation();
    }

    private setUpSliderControls() {
        if (!this.sliderControls?.enable) return;

        const prevBtn = this.parent.querySelector(this.sliderControls.buttonNames.prev);
        const nextBtn = this.parent.querySelector(this.sliderControls.buttonNames.next);
        if (!prevBtn || !nextBtn) return;

        if (this.sliderControls.clicktosnap) {
            this.setUpClickToSnap(prevBtn, nextBtn);
        }

        if (this.sliderControls.clicktoscroll) {
            this.setUpClickToScroll(prevBtn, nextBtn);
        }
    }

    private setUpClickToSnap(prevBtn: Element, nextBtn: Element) {
        const snapConfig = this.sliderControls!.clicktosnap!.config;
        prevBtn.addEventListener('click', () => this.handleClickToSnap('prev', snapConfig));
        nextBtn.addEventListener('click', () => this.handleClickToSnap('next', snapConfig));
    }

    private setUpClickToScroll(prevBtn: Element, nextBtn: Element) {
        const scrollConfig = this.sliderControls!.clicktoscroll!.config;

        if (scrollConfig === 'scroll') {
            this.bindScrollEvents(prevBtn, nextBtn, this.scrollShift.bind(this));
        }

        if (scrollConfig === 'hold') {
            this.bindHoldScrollEvents(prevBtn, nextBtn);
        }
    }

    private bindScrollEvents(prevBtn: Element, nextBtn: Element, scrollFn: (dir: 'left' | 'right') => void) {
        ['mousedown', 'touchstart'].forEach(event => {
            prevBtn.addEventListener(event, () => scrollFn('left'));
            nextBtn.addEventListener(event, () => scrollFn('right'));
        });
    }

    private bindHoldScrollEvents(prevBtn: Element, nextBtn: Element) {
        let interval: number | null = null;

        const startScroll = (dir: 'left' | 'right') => {
            if (interval) clearInterval(interval);
            interval = window.setInterval(() => this.scrollShift(dir), 16);
        };

        const clear = () => {
            if (interval !== null) {
                clearInterval(interval);
                interval = null;
            }
        };

        this.bindScrollEvents(prevBtn, nextBtn, startScroll);

        ['mouseup', 'mouseleave', 'touchend', 'touchcancel'].forEach(event =>
            document.addEventListener(event, clear)
        );

        ['mouseup', 'touchend'].forEach(event => {
            prevBtn.addEventListener(event, clear);
            nextBtn.addEventListener(event, clear);
        });
    }


    private handleClickToSnap(direction: 'prev' | 'next', config: 'page' | number) {
        const numCards = config === 'page'
            ? Math.floor(this.parent.offsetWidth / this.cardWidth)
            : Number(config);

        const scrollAmount = numCards * this.cardWidth * (direction === 'next' ? -1 : 1);
        const currentX = this.getCurrentTranslateX();
        this.setTranslateX(currentX + scrollAmount);
    }

    private scrollShift(direction: 'left' | 'right') {
        const shift = (direction === 'left' ? 1 : -1) * this.cardWidth / this.gap;
        const currentX = this.getCurrentTranslateX();
        const newX = currentX + shift;

        if (!this.Scroll) {
            const minLimit = this.direction === 'left' ? -this.totalSetWidth * 2 : -this.totalSetWidth * 3;
            const maxLimit = this.direction === 'left' ? 0 : -this.totalSetWidth;
            if (newX < minLimit || newX > maxLimit) return;
        }

        this.setTranslateX(newX);
    }

    private setupClones() {
        const cards = Array.from(this.container.children) as HTMLElement[];
        if(!cards) return;
        const cloneBefore = cards.map(card => card.cloneNode(true)).reverse();
        const cloneAfter = cards.map(card => card.cloneNode(true));

        cloneBefore.forEach(c => this.container.insertBefore(c, this.container.firstChild));
        cloneAfter.forEach(c => this.container.appendChild(c));

        this.cardWidth = cards[0].offsetWidth + this.gap;
        this.totalSetWidth = cards.length * this.cardWidth;

        if (this.direction === 'left') {
            this.setTranslateX(-this.totalSetWidth);
        } else {
            this.setTranslateX(-this.totalSetWidth * 2);
        }
    }

    private setupEvents() {
        if (this.dragAndScroll) {
            this.container.addEventListener('mousedown', this.onDragStart);
            document.addEventListener('mousemove', this.onDragMove);
            document.addEventListener('mouseup', this.onDragEnd);

            this.container.addEventListener('touchstart', this.onDragStart, { passive: true });
            document.addEventListener('touchmove', this.onDragMove, { passive: true });
            document.addEventListener('touchend', this.onDragEnd);
        }

        if (this.onClick) {
            this.container.querySelectorAll(this.card).forEach(card => {
                card.addEventListener('click', (e) => this.onCardClick(e.currentTarget as HTMLElement));
            });
        }

        if (this.onHover) {
            this.parent.addEventListener('mouseenter', () => this.pauseAnimation());
            this.parent.addEventListener('mouseleave', () => this.resumeAnimation());
        }
    }

    private getClientX(e: MouseEvent | TouchEvent): number {
        return e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
    }

    private readonly onDragStart = (e: MouseEvent | TouchEvent) => {
        this.pauseAnimation();
        this.isDragging = true;
        this.dragStartX = this.getClientX(e);
    };

    private readonly onDragMove = (e: MouseEvent | TouchEvent) => {
        if (!this.isDragging || this.dragStartX === null) return;
        const currentX = this.getClientX(e);
        const delta = currentX - this.dragStartX;
        this.dragOffset = delta;

        const currentXTransform = this.getCurrentTranslateX();
        this.setTranslateX(currentXTransform + delta);
        this.dragStartX = currentX;
    };

    private readonly onDragEnd = () => {
        if (!this.isDragging) return;
        this.isDragging = false;

        const currentXTransform = this.getCurrentTranslateX();
        const snapOffset = Math.round(currentXTransform / this.cardWidth) * this.cardWidth;
        this.setTranslateX(snapOffset);

        setTimeout(() => this.resumeAnimation(), this.delayAfterStop);
        this.dragOffset = 0;
        this.dragStartX = null;
    };

    private onCardClick(card: HTMLElement) {
        this.pauseAnimation();
        const cardRect = card.getBoundingClientRect();
        const parentRect = this.parent.getBoundingClientRect();
        const centerOffset = cardRect.left - parentRect.left - (parentRect.width - cardRect.width) / 2;

        const currentX = this.getCurrentTranslateX();
        this.setTranslateX(currentX - centerOffset);

        setTimeout(() => this.resumeAnimation(), this.delayAfterStop);
    }

    private getCurrentTranslateX(): number {
        const match = /translateX\((-?\d+(?:\.\d+)?)px\)/.exec(this.container.style.transform || 'translateX(0px)');
        return match ? parseFloat(match[1]) : 0;
    }


    private setTranslateX(value: number) {
        this.container.style.transform = `translateX(${value}px)`;
    }

    private startAnimation() {
        this.lastTimestamp = performance.now();

        const animate = (timestamp: number) => {
            if (this.isPaused) return;

            const delta = timestamp - this.lastTimestamp;
            const shift = this.speed * delta * (this.direction === 'left' ? -1 : 1);
            let newX = this.getCurrentTranslateX() + shift;

            const minLimit = this.direction === 'left' ? -this.totalSetWidth * 2 : -this.totalSetWidth * 3;
            const maxLimit = this.direction === 'left' ? 0 : -this.totalSetWidth;

            if (!this.Scroll) {
                if (newX < minLimit || newX > maxLimit) {
                    this.pauseAnimation();
                    return;
                }
            }

            if (this.direction === 'left' && newX <= -this.totalSetWidth * 2) {
                newX += this.totalSetWidth;
            } else if (this.direction === 'left' && newX >= 0) {
                newX -= this.totalSetWidth;
            }

            if (this.direction === 'right' && newX >= -this.totalSetWidth) {
                newX -= this.totalSetWidth;
            } else if (this.direction === 'right' && newX <= -this.totalSetWidth * 3) {
                newX += this.totalSetWidth;
            }

            this.setTranslateX(newX);
            this.lastTimestamp = timestamp;
            this.animationFrameId = requestAnimationFrame(animate);
        };

        this.animationFrameId = requestAnimationFrame(animate);
    }

    private pauseAnimation() {
        this.isPaused = true;
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    private resumeAnimation() {
        if (!this.isPaused) return;
        this.isPaused = false;
        if (this.autoscroll) this.startAnimation();
    }

    /**
     * Destroys the carousel instance, removing all event listeners and stopping animations.
     * Call this method when the carousel is no longer needed to prevent memory leaks.
     */
    public destroy() {
        // Stop any running animation
        this.pauseAnimation();
        
        // Remove drag and scroll event listeners
        if (this.dragAndScroll) {
            this.container.removeEventListener('mousedown', this.onDragStart);
            document.removeEventListener('mousemove', this.onDragMove);
            document.removeEventListener('mouseup', this.onDragEnd);
            this.container.removeEventListener('touchstart', this.onDragStart);
            document.removeEventListener('touchmove', this.onDragMove);
            document.removeEventListener('touchend', this.onDragEnd);
        }

        // Remove click event listeners from cards
        if (this.onClick) {
            this.container.querySelectorAll(this.card).forEach(card => {
                (card as HTMLElement).removeEventListener('click',(e) => {this.onCardClick(e.target as HTMLElement)});
            });
        }

        // Remove hover event listeners
        if (this.onHover) {
            this.parent.removeEventListener('mouseenter', () => this.pauseAnimation());
            this.parent.removeEventListener('mouseleave', () => this.resumeAnimation());
        }

        // Clean up slider controls if they exist
        if (this.sliderControls?.enable) {
            const prevBtn = this.parent.querySelector(this.sliderControls.buttonNames.prev);
            const nextBtn = this.parent.querySelector(this.sliderControls.buttonNames.next);
            
            if (prevBtn && nextBtn) {
                // Remove click to snap listeners
                if (this.sliderControls.clicktosnap) {
                    prevBtn.removeEventListener('click', () => this.handleClickToSnap('prev', this.sliderControls!.clicktosnap!.config));
                    nextBtn.removeEventListener('click', () => this.handleClickToSnap('next', this.sliderControls!.clicktosnap!.config));
                }
            }
        }

        // Reset container transform
        this.container.style.transform = '';
        
        // Clear any timeouts that might be pending
        // Note: This is a simple approach. For a more robust solution, you might want to track timeouts.
        // However, this will work for the current implementation.
        // @ts-ignore - clearAllTimeouts is not standard but works in some environments
        if (typeof clearAllTimeouts === 'function') {
            // @ts-ignore
            clearAllTimeouts();
        }
    }
}

type SliderControls = {
    /** enbale slider controls, ensure slider buttons exist*/
    enable?: boolean;
    /**button names of next and previous*/
    buttonNames: {
        prev: string;
        next: string;
    };
    /**
     * snaps to fixed cards on display
     * number of cards or
     * by page
     */
    clicktosnap?: {
        config: 'page' | number;
    };
    /**
     * scroll: scrolls the list to a distance for scroll
     * hold: keeps scrolling until the button is pressed
     */
    clicktoscroll?: {
        config: 'scroll' | 'hold';
    };
}

type TemplateName = {
    /**container in which cards are present*/
    container: string;
    /**the cards that are to be scrolled*/
    card: string;
}

/**
 *  carousel configuration
 */
type CarouselConfig = {
    /**parent element of the carousel */
    parent: HTMLElement;
    /** horizontal direction to scroll */
    direction: 'left' | 'right';
    /** delay in ms t stop after click */
    delayAfterStop: number;
    /** if click to stop has to be enabled */
    onclick?: boolean;
    /** if hover to stop has to be enabled */
    onhover?: boolean;
    /** if enabled will load on initialisation */
    autoscroll?: boolean;
    /** if drag to scroll has to be enabled */
    dragAndScroll?: boolean;
    /** speed in px/s */
    speed?: number;
    /** if scroll is to be looped infinitely, if disabled will stop at scroll end */
    infinityScroll?: boolean;
    /** to control scroll as a slider */
    sliderControls?: SliderControls;
    /** uses querySelector to find the elements */
    templateName: TemplateName;
    /** gap between each card, this is required as this makes animation smooth, in px */
    gap: number
}

export { Carousel, CarouselConfig, TemplateName, SliderControls }